// ==============================
// Imports
// ==============================

// MUI Components
import {
  Typography,
  Collapse,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Libraries
import { NumericFormat } from 'react-number-format';
import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

// Hook
import { useServiceAccordionController } from '../hooks/userServiceAccordion';
import dayjs from 'dayjs';
import TreeBranch, { treeItemSx } from './common/TreeBranch';
import { selectedRowBg } from '../utils/rowHighlight';

interface ServiceAccordionProps {
  onClose?: () => void;
}

const ServiceAccordion = ({ onClose }: ServiceAccordionProps) => {
  const [expandedServiceId, setExpandedServiceId] = useState<string | null>(
    null,
  );
  const [expandedSubtypeKey, setExpandedSubtypeKey] = useState<string | null>(
    null,
  );

  const {
    registerLoading,
    dataRes,
    selectedServiceServiceFor,
    selectedSubtype,
    openDialog,
    openDialogFinal,
    formData,
    formErrors,
    formDataFinal,
    formFinalErrors,
    courseLevelOptions,
    courseLevelLoading,
    sos,
    isAddressNotProvided,
    isPhoneNotProvided,
    isWalkInCustomer,
    handleNotProvidingAddress,
    handleNotProvidingPhone,
    handleInputChange,
    handleInputFinalChange,
    handleCourseLevelChange,
    handleSubtypeClick,
    handleSubsubtypeClick,
    // handleDialogOpen,
    handleDialogClose,
    handleDialogConfirm,
    // handleDialogFinalOpen,
    handleDialogFinalClose,
    handleDialogFinalConfirm,
    setSos,
    paymentMethod,
    setPaymentMethod,
  } = useServiceAccordionController();

  const toggleSubtype = (
    serviceName: string,
    subtypeId: string,
    subtypeName: string,
    subsubtypes: Parameters<typeof handleSubtypeClick>[2],
  ) => {
    const subtypeKey = `${serviceName}:${subtypeId}`;
    setExpandedSubtypeKey((currentKey) =>
      currentKey === subtypeKey ? null : subtypeKey,
    );
    handleSubtypeClick(serviceName, subtypeName, subsubtypes);
  };

  const toggleService = (serviceId: string) => {
    setExpandedServiceId((currentId) =>
      currentId === serviceId ? null : serviceId,
    );
    setExpandedSubtypeKey(null);
  };

  // ==============================
  // Render
  // ==============================
  return (
    <>
      <Box
        sx={{
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            pr: 1,
            scrollbarWidth: 'thin',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {dataRes.map((service) => (
              <Box key={service.id}>
                <ListItem
                  disablePadding
                  sx={{
                    mb: 0,
                    backgroundColor: 'background.paper',
                    borderRadius: 0,
                    boxShadow: 'none',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemButton
                    dense
                    onClick={() => toggleService(service.id)}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          variant="h6"
                          component="div"
                          fontWeight="bold"
                          color="text.primary"
                        >
                          {service.name}
                        </Typography>
                      }
                    />
                    {expandedServiceId === service.id ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </ListItemButton>
                </ListItem>

                <Collapse
                  in={expandedServiceId === service.id}
                  timeout="auto"
                  unmountOnExit
                >
                  <TreeBranch sx={{ pt: 2 }}>
                    <Typography
                      variant="body1"
                      component="h3"
                      fontWeight="bold"
                      color="text.primary"
                      sx={{ mb: 2 }}
                    >
                      {service.name} Service Types
                    </Typography>

                    {service.subtypes.map((subtype) => {
                      const subtypeKey = `${service.name}:${subtype.id}`;
                      const isExpanded = expandedSubtypeKey === subtypeKey;
                      const isSelected =
                        selectedSubtype?.serviceName === service.name &&
                        selectedSubtype.subtypeName === subtype.name;

                      return (
                        <Box key={subtype.id}>
                          <ListItem
                            disablePadding
                            sx={[
                              {
                                mb: isExpanded ? 1 : 1,
                                minHeight: 42,
                                backgroundColor: isSelected
                                  ? selectedRowBg
                                  : 'background.paper',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: isSelected ? '#4da7db' : 'divider',
                                '&:hover': {
                                  backgroundColor: isSelected
                                    ? selectedRowBg
                                    : 'action.hover',
                                },
                              },
                              treeItemSx,
                            ]}
                          >
                            <ListItemButton
                              dense
                              onClick={() =>
                                toggleSubtype(
                                  service.name,
                                  subtype.id,
                                  subtype.name,
                                  subtype.subsubtypes,
                                )
                              }
                              sx={{ minHeight: 40 }}
                            >
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body2"
                                    fontWeight={isSelected ? 700 : 400}
                                    color="text.primary"
                                  >
                                    {subtype.name}
                                  </Typography>
                                }
                              />
                              {isExpanded ? (
                                <ChevronUp size={18} />
                              ) : (
                                <ChevronDown size={18} />
                              )}
                            </ListItemButton>
                          </ListItem>

                          <Collapse
                            in={isExpanded}
                            timeout="auto"
                            unmountOnExit
                          >
                            <TreeBranch sx={{ mb: 1 }}>
                              <Typography
                                variant="body2"
                                component="h4"
                                fontWeight="bold"
                                color="text.primary"
                                sx={{ mb: 1.5 }}
                              >
                                {subtype.name} Packages
                              </Typography>

                              {subtype.subsubtypes.map((item) => (
                                <ListItem
                                  key={item.id}
                                  disablePadding
                                  sx={[
                                    {
                                      mb: 1,
                                      minHeight: 42,
                                      backgroundColor: 'background.paper',
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      '&:hover': {
                                        backgroundColor: 'action.hover',
                                      },
                                    },
                                    treeItemSx,
                                  ]}
                                >
                                  <ListItemButton
                                    dense
                                    onClick={() => handleSubsubtypeClick(item)}
                                    sx={{ minHeight: 40 }}
                                  >
                                    <ListItemText
                                      primary={
                                        <Typography
                                          variant="body2"
                                          color="text.primary"
                                        >
                                          {item.name}
                                        </Typography>
                                      }
                                    />
                                  </ListItemButton>
                                </ListItem>
                              ))}
                            </TreeBranch>
                          </Collapse>
                        </Box>
                      );
                    })}
                  </TreeBranch>
                </Collapse>
              </Box>
            ))}
          </Box>
        </Box>

        {onClose && (
          <Box
            display="flex"
            justifyContent="flex-end"
            sx={{
              flexShrink: 0,
              mt: 1,
              pt: 1,
              px: 1,
              pb: 0.5,
              backgroundColor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              position: 'sticky',
              bottom: 0,
              zIndex: 1,
            }}
          >
            <Button
              variant="contained"
              className="!text-white"
              color="error"
              onClick={onClose}
            >
              Close
            </Button>
          </Box>
        )}
      </Box>

      {/* Other components */}
      {/* Dialog 1*/}
      {/* Yes/ No dialog */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add / Update additional Information</DialogTitle>

        {/*  */}
        {/* Additional information */}
        {/*  */}
        <div className="flex flex-col gap-2 mx-4">
          {isWalkInCustomer && (
            <TextField
              id="tf-walk-in-customer-name"
              label="Walk-in Customer Name"
              variant="standard"
              name="walkInCustomerName"
              value={formData.walkInCustomerName}
              onChange={handleInputChange}
              error={Boolean(formErrors.walkInCustomerName)}
              helperText={
                formErrors.walkInCustomerName ||
                'Optional. Used for registration lookup and invoice display.'
              }
            />
          )}

          {selectedServiceServiceFor == 'CHILD' ? (
            <>
              <TextField
                id="tf-parent"
                label="Parent Name"
                variant="standard"
                name="parent"
                value={formData.parent}
                onChange={handleInputChange}
                error={Boolean(formErrors.parent)}
                helperText={formErrors.parent}
              />
            </>
          ) : (
            <></>
          )}

          <Box display="flex" flexDirection="column" gap={1}>
            <TextField
              id="tf-phone"
              className={!isWalkInCustomer ? 'required-field' : undefined}
              label="Phone"
              variant="standard"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              error={Boolean(formErrors.phone)}
              helperText={formErrors.phone}
            />

            {!isWalkInCustomer && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={isPhoneNotProvided}
                    onChange={handleNotProvidingPhone}
                    name="isPhoneNotProvided"
                  />
                }
                label="Customer not providing phone number"
              />
            )}
          </Box>

          <Box display="flex" flexDirection="column" gap={1} mt={1}>
            <TextField
              id="tf-address"
              className={!isWalkInCustomer ? 'required-field' : undefined}
              label="Address"
              variant="standard"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              error={Boolean(formErrors.address)}
              helperText={formErrors.address}
            />

            {!isWalkInCustomer && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={isAddressNotProvided}
                    onChange={handleNotProvidingAddress}
                    name="isAddressNotProvided"
                  />
                }
                label="Customer not providing address"
              />
            )}
          </Box>

          <TextField
            id="tf-email"
            label="Email"
            variant="standard"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            error={Boolean(formErrors.email)}
            helperText={formErrors.email}
          />
          <TextField
            id="tf-idCard"
            label={
              localStorage.getItem('selectedUserType') === 'BUSINESS'
                ? 'Identity Number'
                : 'Identity Number'
            }
            variant="standard"
            name="idCard"
            value={formData.idCard}
            onChange={handleInputChange}
            error={Boolean(formErrors.idCard)}
            helperText={formErrors.idCard}
          />
          <TextField
            id="tf-taxCode"
            className={
              localStorage.getItem('selectedUserType') === 'BUSINESS'
                ? 'required-field'
                : undefined
            }
            label="Tax Code"
            variant="standard"
            name="taxCode"
            value={formData.taxCode}
            onChange={handleInputChange}
            error={Boolean(formErrors.taxCode)}
            helperText={formErrors.taxCode}
          />
        </div>
        {/*  */}
        {/* Additional information */}
        {/*  */}
        <DialogActions>
          <Button onClick={handleDialogClose} variant="contained" color="error">
            Close
          </Button>
          <Button
            onClick={handleDialogConfirm}
            variant="contained"
            color="primary"
            className="!text-white"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog 2 */}
      {/* Final confirmation */}
      <Dialog
        open={openDialogFinal}
        onClose={handleDialogFinalClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Confirm Registration</DialogTitle>
        <Box ml={3} mt={2} display="flex" flexDirection="column" gap={1}>
          <Box display="flex">
            <Box width="30%">
              <Typography variant="subtitle2" fontWeight="bold">
                NAME:
              </Typography>
            </Box>
            <Box width="70%">
              <Typography variant="body1">
                {localStorage.getItem('selectedUserFullName')}
              </Typography>
            </Box>
          </Box>

          <Box display="flex">
            <Box width="30%">
              <Typography variant="subtitle2" fontWeight="bold">
                ID:
              </Typography>
            </Box>
            <Box width="70%">
              <Typography variant="body1">
                {localStorage.getItem('selectedUserId')}
              </Typography>
            </Box>
          </Box>
          <Box display="flex">
            <Box width="30%">
              <Typography variant="subtitle2" fontWeight="bold">
                DOB:
              </Typography>
            </Box>
            <Box width="70%">
              <Typography variant="body1">
                {(() => {
                  const dobString = localStorage.getItem('selectedUserDob');
                  if (!dobString) return '--/--/----';
                  const dob = new Date(dobString);
                  return isNaN(dob.getTime())
                    ? 'Something went wrong.'
                    : dob.toLocaleDateString('vi-VN');
                })()}
              </Typography>
            </Box>
          </Box>
          {selectedSubtype?.serviceName === 'COURSE' &&
            (courseLevelLoading || courseLevelOptions.length > 0) && (
              <Box display="flex" alignItems="flex-start" mt={1}>
                <Box width="30%" pt={1}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    LEVEL:
                  </Typography>
                </Box>
                <Box width="70%" pr={2}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    required
                    error={Boolean(formFinalErrors.courseLevel)}
                    disabled={courseLevelLoading}
                  >
                    <InputLabel id="course-level-label">
                      Course Level
                    </InputLabel>
                    <Select
                      labelId="course-level-label"
                      label="Course Level"
                      value={formDataFinal.courseLevel}
                      onChange={(event) =>
                        handleCourseLevelChange(event.target.value as string)
                      }
                    >
                      <MenuItem value="">
                        <em>
                          {courseLevelLoading
                            ? 'Loading levels...'
                            : 'Select course level'}
                        </em>
                      </MenuItem>
                      {courseLevelOptions.map((level) => (
                        <MenuItem key={level.level} value={level.levelName}>
                          {level.levelName}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {formFinalErrors.courseLevel ||
                        'Select a level declared for this course service.'}
                    </FormHelperText>
                  </FormControl>
                </Box>
              </Box>
            )}
          <Box display="flex">
            <Box width="30%">
              <Typography variant="subtitle2" fontWeight="bold">
                PACKAGE:
              </Typography>
            </Box>
            <Box width="70%">
              <Typography variant="body1">
                {localStorage.getItem('selectedServiceName')}
              </Typography>
            </Box>
          </Box>
          <Box display="flex">
            <Box width="30%">
              <Typography variant="subtitle2" fontWeight="bold">
                PRICE:
              </Typography>
            </Box>
            <Box width="70%">
              <Typography variant="body1">
                {new Intl.NumberFormat('vi-VN', {
                  style: 'currency',
                  currency: 'VND',
                  maximumFractionDigits: 0,
                }).format(Number(localStorage.getItem('selectedServicePrice')))}
              </Typography>
            </Box>
          </Box>
          {localStorage.getItem('selectedServiceServiceFor') === 'GROUP' && (
            <Box display="flex">
              <Box width="30%">
                <Typography variant="subtitle2" fontWeight="bold">
                  PRICE (For Child):
                </Typography>
              </Box>
              <Box width="70%">
                <Typography variant="body1">
                  {new Intl.NumberFormat('vi-VN', {
                    style: 'currency',
                    currency: 'VND',
                    maximumFractionDigits: 0,
                  }).format(
                    Number(
                      localStorage.getItem('selectedServicePriceForChild'),
                    ),
                  )}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        <Box ml={3} mt={2} width="90%" display="flex" flexDirection="column">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Select service start date"
              value={sos}
              onChange={(newDate) => setSos(newDate)}
              onOpen={() => setSos(dayjs().startOf('day'))}
            />
          </LocalizationProvider>
          {formFinalErrors.sos && (
            <Typography variant="caption" color="error" mt={1}>
              {formFinalErrors.sos}
            </Typography>
          )}
        </Box>
        <Box mt={2} ml={3} width="90%">
          <FormControl fullWidth variant="outlined">
            <InputLabel id="payment-method-label">Payment Method</InputLabel>
            <Select
              name="paymentMethod"
              labelId="payment-method-label"
              value={paymentMethod}
              onChange={(e) => {
                setPaymentMethod(e.target.value as string);
              }}
              label="Payment Method"
            >
              <MenuItem value="CASH">Cash</MenuItem>
              <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
              <MenuItem value="DEPOSIT">Deposit</MenuItem>
            </Select>
            {formFinalErrors.paymentMethod && (
              <Typography variant="caption" color="error" mt={1}>
                {formFinalErrors.paymentMethod}
              </Typography>
            )}
          </FormControl>
        </Box>
        <Box mt={2} ml={3} width="90%">
          {/* <TextField
              id="tf-discountPrice"
              label="Discount Amount"
              variant="standard"
              name="discountPrice"
              value={formDataFinal.discountPrice}
              onChange={handleInputFinalChange}
              fullWidth
              type="number"
              error={Boolean(formFinalErrors.discountPrice)}
              helperText={formFinalErrors.discountPrice}
            /> */}
          <NumericFormat
            customInput={TextField}
            thousandSeparator
            id="tf-discountPrice"
            label="Discount Amount"
            variant="standard"
            name="discountPrice"
            value={formDataFinal.discountPrice}
            onChange={handleInputFinalChange}
            fullWidth
            type="text"
            error={Boolean(formFinalErrors.discountPrice)}
            helperText={formFinalErrors.discountPrice}
          />
        </Box>
        <Box mt={2} ml={3} width="90%">
          <TextField
            id="tf-amount"
            label={
              localStorage.getItem('selectedServiceServiceFor') === 'GROUP'
                ? 'Adult Quantity'
                : 'Quantity'
            }
            variant="standard"
            name="amount"
            value={formDataFinal.amount}
            onChange={handleInputFinalChange}
            fullWidth
            type="number"
            error={Boolean(formFinalErrors.amount)}
            helperText={formFinalErrors.amount}
          />
        </Box>
        {localStorage.getItem('selectedServiceServiceFor') === 'GROUP' && (
          <Box mt={2} ml={3} width="90%">
            <TextField
              id="tf-subAmount"
              label="Child Quantity"
              variant="standard"
              name="subAmount"
              value={formDataFinal.subAmount}
              onChange={handleInputFinalChange}
              fullWidth
              type="number"
              error={Boolean(formFinalErrors.subAmount)}
              helperText={formFinalErrors.subAmount}
            />
          </Box>
        )}
        <Box mt={2} ml={3} width="90%">
          <TextField
            id="tf-note"
            label="Note"
            variant="standard"
            name="note"
            value={formDataFinal.note}
            onChange={handleInputFinalChange}
            multiline
            minRows={1}
            fullWidth
            error={Boolean(formFinalErrors.note)}
            helperText={formFinalErrors.note}
          />
        </Box>
        <DialogActions>
          <Button
            onClick={handleDialogFinalClose}
            variant="contained"
            color="error"
          >
            Close
          </Button>
          <Button
            loading={registerLoading}
            onClick={handleDialogFinalConfirm}
            variant="contained"
            color="primary"
            className="!text-white"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ServiceAccordion;
